import { getOvertimePeriods } from './GameOverview.dataManipulation';

// Play node's `homeScore` / `awayScore` are the score ENTERING the play.
// To find what a play earned, diff against the next play's pre-play score
// (or the game's final score for the last play in the list).
const XP_TYPE = 'EXTRA_POINT';
const TWO_POINT_TYPE = 'TWO_POINT_CONVERSION';

// Points a play earned: the score diff against the next play (or the game's
// final score for the last play).
const pointsEarned = (play, nextPlay, game) => {
  const preHome = play.homeScore ?? 0;
  const preAway = play.awayScore ?? 0;
  const postHome = nextPlay?.homeScore ?? game.homeScore ?? preHome;
  const postAway = nextPlay?.awayScore ?? game.awayScore ?? preAway;
  return { homeDelta: postHome - preHome, awayDelta: postAway - preAway };
};

// The try that follows a touchdown: { kind: 'XP' | '2PT', good: boolean }.
// `success` is authoritative when present, else fall back to the score diff.
const getTry = (tryPlay, nextPlay, game) => {
  if (tryPlay?.type !== XP_TYPE && tryPlay?.type !== TWO_POINT_TYPE) {
    return null;
  }
  const { homeDelta, awayDelta } = pointsEarned(tryPlay, nextPlay, game);
  return {
    kind: tryPlay.type === XP_TYPE ? 'XP' : '2PT',
    good:
      typeof tryPlay.success === 'boolean'
        ? tryPlay.success
        : Math.max(homeDelta, awayDelta) > 0,
  };
};

// Plays that can sit between a touchdown and its try without being a play of
// the game: penalties, timeouts, stoppages and off-camera plays.
const isInterruption = (play) =>
  play.type?.startsWith('NO_PLAY') || play.type === 'OFF_CAMERA';

// Index of the try that belongs to the touchdown at `idx`, looking past any
// interruptions; -1 when the next real play is not a try (end of half, missing
// data).
const findTryIndex = (sorted, idx) => {
  for (let i = idx + 1; i < sorted.length; i += 1) {
    const { type } = sorted[i];
    if (type === XP_TYPE || type === TWO_POINT_TYPE) return i;
    if (!isInterruption(sorted[i])) return -1;
  }
  return -1;
};

// Result suffix for a scoring row's try; null when there is no try to show.
export const formatTryResult = (extraPoint) =>
  extraPoint
    ? `${extraPoint.kind} ${extraPoint.good ? 'good' : 'no good'}`
    : null;

export const getScoringPlays = (plays, game) => {
  if (!plays?.length || !game) return [];

  const homeId = game.homeTeam?.id;
  const awayId = game.awayTeam?.id;

  const sorted = [...plays].sort(
    (a, b) => (a.gameIndex ?? 0) - (b.gameIndex ?? 0),
  );

  const overtimePeriods = getOvertimePeriods(sorted);
  const scoring = [];
  // Tries already attached to a touchdown row.
  const attachedTries = new Set();

  sorted.forEach((play, idx) => {
    const isTry = play.type === XP_TYPE || play.type === TWO_POINT_TYPE;
    // Tries are not rows of their own when they follow a touchdown: they are
    // attached to it (see extraPoint below). A try nothing claimed is a play
    // in its own right (e.g. college overtime from the third period, which is
    // two-point tries only) and falls through to be scored like any other.
    if (isTry && attachedTries.has(idx)) return;

    const nextPlay = sorted[idx + 1];
    const { homeDelta, awayDelta } = pointsEarned(play, nextPlay, game);

    if (homeDelta <= 0 && awayDelta <= 0) return;

    const isHome = homeDelta > awayDelta;
    // null when no try follows (end of half, missing data).
    const tryIdx = isTry ? -1 : findTryIndex(sorted, idx);
    if (tryIdx >= 0) attachedTries.add(tryIdx);
    const tryPlay = tryIdx >= 0 ? sorted[tryIdx] : null;
    const afterTry = tryIdx >= 0 ? sorted[tryIdx + 1] : null;
    const extraPoint = tryPlay ? getTry(tryPlay, afterTry, game) : null;

    // The row's score is the score once the touchdown's try is settled, so a
    // good kick reads 7 - 0, not 6 - 0. With no try it is the post-touchdown
    // score.
    const scoreAfter = tryPlay ? afterTry : nextPlay;
    const postHome =
      scoreAfter?.homeScore ?? game.homeScore ?? play.homeScore ?? 0;
    const postAway =
      scoreAfter?.awayScore ?? game.awayScore ?? play.awayScore ?? 0;

    scoring.push({
      id: play.id,
      quarter: play.quarter,
      overtimePeriod: overtimePeriods.get(play.id) ?? null,
      quarterClockRemaining: play.quarterClockRemaining,
      down: play.down,
      yardsToGo: play.yardsToGo,
      type: play.type,
      name: play.name,
      driveResult: play.drive?.name?.split('→ ')[1]?.trim() || '',
      yardsNet: play.yardsNet,
      yardLine: play.yardLine,
      scrimmageEpa: play.scrimmageEpa,
      explosive: play.explosive,
      homeScore: postHome,
      awayScore: postAway,
      teamId: isHome ? homeId : awayId,
      isHome,
      extraPoint,
      raw: play,
    });
  });

  return scoring;
};
